/** Standalone Maison purchase form. Reconnecting in the theme editor replaces listeners. */
(() => {
  if (customElements.get('maison-featured-product')) return;

  customElements.define('maison-featured-product', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.form = this.querySelector('form');
      this.selector = this.querySelector('[data-variant]');
      this.quantity = this.querySelector('[data-quantity]');
      this.button = this.querySelector('[data-submit]');
      this.status = this.querySelector('[data-status]');
      if (!this.form || !this.selector || !this.quantity || !this.button) return;

      try {
        this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      } catch (_) {
        // The standard Shopify form remains available if enhancement cannot start.
        return;
      }

      this.selector.addEventListener('change', () => this.updateVariant(), { signal });
      this.form.addEventListener('submit', (event) => this.add(event), { signal });
      this.updateVariant();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    currentVariant() {
      return this.variants.find((variant) => String(variant.id) === this.selector.value);
    }

    updateVariant() {
      const variant = this.currentVariant();
      const available = Boolean(variant?.available);
      const price = this.querySelector('[data-price]');
      const compare = this.querySelector('[data-compare]');
      const stock = this.querySelector('[data-stock]');
      if (price) price.textContent = variant?.price || '';
      if (compare) {
        compare.textContent = variant?.compare || '';
        compare.hidden = !variant?.compare;
      }
      if (stock) stock.textContent = available ? this.dataset.availableLabel : this.dataset.soldOutLabel;
      this.button.disabled = this.busy || !available;
      this.button.textContent = this.busy ? this.dataset.addingLabel : available ? this.dataset.addLabel : this.dataset.soldOutLabel;
      if (this.status) this.status.hidden = true;
      if (!variant) return;

      const minimum = Math.max(1, Number(variant.minimum) || 1);
      const increment = Math.max(1, Number(variant.increment) || 1);
      const maximum = variant.maximum == null ? Infinity : Number(variant.maximum);
      this.quantity.min = String(minimum);
      this.quantity.step = String(increment);
      if (Number.isFinite(maximum)) this.quantity.max = String(maximum);
      else this.quantity.removeAttribute('max');
      // Keep quantities aligned with the selected variant's rules, including when mounts change.
      let quantity = Math.max(minimum, Math.floor(Number(this.quantity.value) || minimum));
      quantity = minimum + Math.floor((quantity - minimum) / increment) * increment;
      if (Number.isFinite(maximum)) {
        quantity = Math.min(quantity, minimum + Math.floor((maximum - minimum) / increment) * increment);
      }
      this.quantity.value = String(Math.max(minimum, quantity));
      if (maximum < minimum) this.button.disabled = true;
    }

    async add(event) {
      event.preventDefault();
      const variant = this.currentVariant();
      if (this.busy || !variant?.available || !this.form.reportValidity()) return;
      const quantity = Number(this.quantity.value);
      if (!Number.isSafeInteger(quantity) || quantity < 1) return;

      this.busy = true;
      this.button.disabled = true;
      this.button.setAttribute('aria-busy', 'true');
      this.button.textContent = this.dataset.addingLabel;
      if (this.status) this.status.hidden = true;
      let added = false;
      try {
        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true, detail: { sections },
        }));
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity }],
            sections: [...new Set(sections)].join(','),
            sections_url: `${this.dataset.variantUrlBase}${variant.id}`,
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.errorLabel);
        added = true;
        if (this.dataset.cartType === 'page' || this.dataset.pageType === 'cart') {
          window.location.assign(this.dataset.cartPageUrl);
          return;
        }
        // A cart refresh error must not tell the shopper that a successful add failed.
        try {
          const cartResponse = await fetch(this.dataset.cartUrl);
          if (!cartResponse.ok) throw new Error('Cart refresh failed');
          const cart = await cartResponse.json();
          cart.sections = result.sections;
          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true, detail: { items: result.items || [result], cart },
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true, detail: { baseEvent: 'variant:add', cart },
          }));
        } catch (_) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }
      } catch (error) {
        if (this.isConnected && this.status) {
          this.status.textContent = error.message || this.dataset.errorLabel;
          this.status.hidden = false;
        }
      } finally {
        this.busy = false;
        if (this.isConnected) {
          this.button.removeAttribute('aria-busy');
          const current = this.currentVariant();
          const invalidQuantityRules = current?.maximum != null && Number(current.maximum) < Math.max(1, Number(current.minimum) || 1);
          this.button.disabled = !current?.available || invalidQuantityRules;
          this.button.textContent = current?.available ? this.dataset.addLabel : this.dataset.soldOutLabel;
          if (added && this.status) {
            this.status.textContent = this.dataset.addedLabel;
            this.status.hidden = false;
          }
        }
      }
    }
  });
})();
